import { apiGet, apiPost, apiRequest } from './api';

// ==================== 类型定义 ====================

export interface TagEntity {
  id: number;
  name: string;
  description?: string;
  tagType: string;  // topic/sentiment/keyword/category
  source: string;   // manual/ai/rule
  usageCount: number;
  createTime: string;
  updateTime: string;
}

export interface CreateTagRequest {
  name: string;
  tagType: string;
  description?: string;
  source?: string;
}

export interface UpdateTagRequest {
  name?: string;
  description?: string;
}

export interface ApiResponse<T = any> {
  code: number;
  msg: string;
  data: T;
}

export interface BotAccount {
  id: number;
  walletAddress: string;
  userName: string;
  avatar?: string;
  tags?: string[];
}

export interface NewsRaw {
  id: number;
  title: string;
  content: string;
  tags?: string[];
  createTime: string;
}

// ==================== 标签定义管理 ====================

/**
 * 获取所有标签
 */
export async function getTagList(): Promise<TagEntity[]> {
  const res = await apiGet<ApiResponse<TagEntity[]>>('/api/tag/list');
  return res.data || [];
}

/**
 * 按类型查询标签
 */
export async function getTagsByType(tagType: string): Promise<TagEntity[]> {
  const res = await apiGet<ApiResponse<TagEntity[]>>(`/api/tag/type/${tagType}`);
  return res.data || [];
}

/**
 * 搜索标签
 */
export async function searchTags(keyword: string, limit: number = 20): Promise<TagEntity[]> {
  const res = await apiGet<ApiResponse<TagEntity[]>>(`/api/tag/search?keyword=${encodeURIComponent(keyword)}&limit=${limit}`);
  return res.data || [];
}

/**
 * 获取热门标签
 */
export async function getTopTags(limit: number = 20): Promise<TagEntity[]> {
  const res = await apiGet<ApiResponse<TagEntity[]>>(`/api/tag/top?limit=${limit}`);
  return res.data || [];
}

/**
 * 创建标签
 */
export async function createTag(data: CreateTagRequest): Promise<TagEntity> {
  const res = await apiPost<ApiResponse<TagEntity>>('/api/tag', data);
  return res.data;
}

/**
 * 更新标签
 */
export async function updateTag(id: number, data: UpdateTagRequest): Promise<void> {
  await apiRequest<ApiResponse>(`/api/tag/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * 删除标签
 */
export async function deleteTag(id: number): Promise<void> {
  await apiRequest<ApiResponse>(`/api/tag/${id}`, {
    method: 'DELETE',
  });
}

// ==================== 机器人标签管理 ====================

/**
 * 获取机器人标签
 */
export async function getBotTags(botId: number): Promise<string[]> {
  const res = await apiGet<ApiResponse<string[]>>(`/api/tag/bot/${botId}/tags`);
  return res.data || [];
}

/**
 * 设置机器人标签
 */
export async function setBotTags(botId: number, tags: string[]): Promise<void> {
  await apiRequest<ApiResponse>(`/api/tag/bot/${botId}/tags`, {
    method: 'PUT',
    body: JSON.stringify({ tags }),
  });
}

/**
 * 按标签搜索机器人
 */
export async function searchBotsByTags(tags: string[]): Promise<BotAccount[]> {
  const res = await apiPost<ApiResponse<BotAccount[]>>('/api/tag/bot/search', { tags });
  return res.data || [];
}

// ==================== 资讯标签管理 ====================

/**
 * 获取资讯标签
 */
export async function getNewsTags(newsId: number): Promise<string[]> {
  const res = await apiGet<ApiResponse<string[]>>(`/api/tag/news/${newsId}/tags`);
  return res.data || [];
}

/**
 * 设置资讯标签
 */
export async function setNewsTags(newsId: number, tags: string[]): Promise<void> {
  await apiRequest<ApiResponse>(`/api/tag/news/${newsId}/tags`, {
    method: 'PUT',
    body: JSON.stringify({ tags }),
  });
}

/**
 * 按标签搜索资讯
 */
export async function searchNewsByTags(tags: string[]): Promise<NewsRaw[]> {
  const res = await apiPost<ApiResponse<NewsRaw[]>>('/api/tag/news/search', { tags });
  return res.data || [];
}

// ==================== 标签类型常量 ====================

export const TAG_TYPES = [
  { value: 'topic', label: '话题', color: 'cyan' },
  { value: 'sentiment', label: '情感', color: 'magenta' },
  { value: 'keyword', label: '关键词', color: 'green' },
  { value: 'category', label: '分类', color: 'orange' },
] as const;

export const TAG_SOURCES = [
  { value: 'manual', label: '手动创建' },
  { value: 'ai', label: 'AI生成' },
  { value: 'rule', label: '规则提取' },
] as const;

export function getTagTypeColor(tagType: string): string {
  const type = TAG_TYPES.find(t => t.value === tagType);
  return type?.color || 'default';
}

export function getTagTypeLabel(tagType: string): string {
  const type = TAG_TYPES.find(t => t.value === tagType);
  return type?.label || tagType;
}

export function getTagSourceLabel(source: string): string {
  const src = TAG_SOURCES.find(s => s.value === source);
  return src?.label || source;
}
